"use client"

import { Button, Typography } from "@mui/material"
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles"
import { trackTransaction } from "common/analytics"
import NextLink from "common/bit/NextLink"
import * as authRepository from "components/shopFeatures/auth/authRepository"
import { initAuth } from "components/shopFeatures/auth/authSlice"
import { selectedDetailingSelector } from "components/shopFeatures/detailing/detailingSelectors"
import { getColorImages } from "components/shopFeatures/summary/SummaryGallery"
import {
  CONTINUE_PURCHASE_LINK,
  Font,
  MODEL_NAME,
} from "components/shopFeatures/wizard/constants"
import { selectedComponentsForAnalyticsSelector } from "components/shopFeatures/wizard/wizardSelectors"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import successPurchaseAnimation from "public/animation/success-purchase.json"
import { useEffect } from "react"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import shopTheme from "theme/shopTheme"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
`
const StyledLottie = styled(Lottie)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: auto;
  z-index: 50;
  pointer-events: none;
`
const StyledInnerContainer = styled.div`
  margin-top: 140px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 530px;
`
const StyledImage = styled.img`
  width: 100%;
`
const StyledSubHeading = styled(Typography).attrs({
  variant: "body1",
  // component: "h1",
})`
  text-align: center;
  font-family: ${Font.OENaPro};
  font-size: 28px;
  font-weight: 400;
  line-height: 1.4;
`
const StyledModelName = styled.span`
  font-family: ${Font.MazdaType};
`
const StyledText = styled(Typography)`
  font-size: 18px;
  font-weight: 300;
  line-height: 27px;
  text-align: center;
  margin-top: 16px;
`
const StyledSubText = styled(Typography)`
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  text-align: center;
  margin-top: 16px;
`

const StyledLink = styled(NextLink)`
  display: flex;
  justify-content: center;
`

const StyledButton = styled(Button)`
  width: 328px;
  height: 45px;
  background: black;
  color: white;
  margin: 30px auto 0;
  font-weight: 400;

  :hover,
  :focus {
    background: ${(p) => p?.theme?.colors?.darkGrey};
    color: white;
    border: none !important;
    outline: none !important;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 40px;
  }
`

export default function SuccessPage() {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const { transactionComplete, outOfStockComplete } = useTypedSelector(
    (state) => state.payment
  )
  const detailing = useTypedSelector(selectedDetailingSelector)
  const selectedComponentsForAnalytics = useTypedSelector(
    selectedComponentsForAnalyticsSelector
  )
  const { userInfo: registeredUserInfo, initialized } = useTypedSelector(
    (state) => state.auth
  )
  const state = useTypedSelector((state) => state)

  // Initialize auth from localStorage on mount
  useEffect(() => {
    async function initializeAuth() {
      const token = await authRepository.getToken()
      if (token) {
        dispatch(initAuth(token))
      }
    }
    initializeAuth()
  }, [dispatch])

  // ---------- Effects ----------
  useEffect(() => {
    localStorage.removeItem(CONTINUE_PURCHASE_LINK)
    localStorage.removeItem(MODEL_NAME)

    if (outOfStockComplete) return
    if (!transactionComplete || !detailing || !selectedComponentsForAnalytics) {
      router.replace("/")
      return
    }

    trackTransaction(
      selectedComponentsForAnalytics,
      transactionComplete.orderId
    )
  }, [transactionComplete, detailing, selectedComponentsForAnalytics])

  // Only redirect if auth is initialized and user is not logged in
  useEffect(() => {
    if (!initialized) return // Wait for auth to initialize

    if (!registeredUserInfo) {
      router.replace("/models/shop/")
    }
  }, [registeredUserInfo, initialized, router])

  if (!initialized || !registeredUserInfo) return null

  const modelName = detailing?.details?.modelName
  const carImages = getColorImages(state)
  const pathToCarImage = carImages?.details?.images[3]

  return (
    <MuiThemeProvider theme={shopTheme}>
      {!outOfStockComplete && (
        <StyledLottie animationData={successPurchaseAnimation} loop autoplay />
      )}

      <StyledContainer>
        <StyledInnerContainer>
          <StyledImage
            src={pathToCarImage?.imageUrl}
            alt={pathToCarImage?.alt || ""}
          />

          {outOfStockComplete ? (
            <StyledSubHeading>
              {registeredUserInfo.firstName}, ה-
              <StyledModelName>{modelName}</StyledModelName> כבר כמעט שלך
            </StyledSubHeading>
          ) : (
            <StyledSubHeading>
              {registeredUserInfo.firstName}, מזל טוב!
              <br />
              מאזדה חדשה בדרך אליך
            </StyledSubHeading>
          )}

          {outOfStockComplete ? (
            <StyledText>
              חלק מהאפשרויות שבחרת לא זמינות כרגע. נחזור אליך בקרוב עם אפשרויות
              מתאימות עבורך.
            </StyledText>
          ) : (
            <StyledText>
              תודה רבה, קיבלנו את המקדמה שלך.
              <br />
              ניצור איתך קשר ממש בקרוב כדי לאשר את פרטי ההזמנה.
              <br />
              אחרי שנבדוק יחד שהכול בדיוק כמו שבחרת, נמשיך לתשלום המלא.
            </StyledText>
          )}

          {!outOfStockComplete && transactionComplete && (
            <StyledSubText>
              מספר הזמנה: {transactionComplete.orderId}
            </StyledSubText>
          )}

          <StyledLink href="/">
            <StyledButton variant="contained" color="primary">
              חזרה לאתר מאזדה
            </StyledButton>
          </StyledLink>
        </StyledInnerContainer>
      </StyledContainer>
    </MuiThemeProvider>
  )
}
